/**
 * Enliple OneTag(모비온) 비쇼핑 피드 — 상품/전환 코드 카탈로그
 *
 * SPA: head에 tcs.js 1회 + 라우터 변경마다 CallMtm() (중복 삽입 금지)
 * @see https://enliple.notion.site/SPA-f03f16dbd0d14139ac71f3bfd7037c79
 * @see https://enliple.notion.site/0f60fb7a8bb648b38560359526225276
 *
 * 모비온 담당자와 아래 productCode / productName 값을 공유하면
 * 대시보드에서 메뉴 라우트·앱 다운로드·책 만들기 전환을 구분할 수 있습니다.
 */

export type OneTagConvType = 'etc' | 'join' | 'login'

export type OneTagRouteProduct = {
  /** CallMtm productCode — 모비온 협의용 고정 코드 */
  productCode: string
  /** 내부 설명 (트래킹에 전송되지 않음) */
  label: string
}

export type OneTagConversion = {
  /** CallMtm productName — 전환 식별값 */
  productName: string
  convType: OneTagConvType
  /** 내부 설명 */
  label: string
  /** 클릭 전환용 CSS selector (선택) */
  clickSelector?: string
}

/** index.html OneTag 스크립트 eid 와 동일 */
export const ONETAG_DEFAULT_EID = 'tofp4xqc1c9xtofp4xqc1c'

/**
 * 상단 메뉴·주요 랜딩 라우트 → 비쇼핑 상품 코드
 * SPA 라우트 변경 시 CallMtm({ productCode }) 1회 전송 (bare CallMtm 과 중복 호출 안 함)
 */
export const ONETAG_ROUTE_PRODUCTS: Record<string, OneTagRouteProduct> = {
  '/': { productCode: 'st_home', label: '홈' },
  '/brand-story': { productCode: 'st_brand_story', label: '브랜드 스토리' },
  '/service/features': {
    productCode: 'st_service_features',
    label: '서비스 · 기능',
  },
  '/service/pricing': {
    productCode: 'st_service_pricing',
    label: '서비스 · 요금',
  },
  '/service/comparison': {
    productCode: 'st_service_comparison',
    label: '서비스 · 비교',
  },
  '/use-cases': { productCode: 'st_use_cases', label: '활용 사례 허브' },
  '/use-cases/parents': {
    productCode: 'st_use_cases_parents',
    label: '활용 사례 · 부모님',
  },
  '/use-cases/daycare': {
    productCode: 'st_use_cases_daycare',
    label: '활용 사례 · 어린이집',
  },
  '/use-cases/events': {
    productCode: 'st_use_cases_events',
    label: '활용 사례 · 행사',
  },
  '/use-cases/seniors': {
    productCode: 'st_use_cases_seniors',
    label: '활용 사례 · 어르신',
  },
  '/blog/insights': {
    productCode: 'st_blog_insights',
    label: '블로그 · 인사이트',
  },
  '/blog/updates': {
    productCode: 'st_blog_updates',
    label: '블로그 · 서비스 소식',
  },
  '/blog/faq': { productCode: 'st_blog_faq', label: '블로그 · FAQ' },
  '/contact': { productCode: 'st_contact', label: '문의하기' },
  '/start': {
    productCode: 'st_start_book_creation',
    label: '시작하기 → 책 만들기 리다이렉트',
  },
  '/terms': { productCode: 'st_terms', label: '이용약관' },
  '/privacy': { productCode: 'st_privacy', label: '개인정보처리방침' },
  '/youth': { productCode: 'st_youth', label: '청소년보호정책' },
  '/cookie-policy': { productCode: 'st_cookie_policy', label: '쿠키 정책' },
}

/** 동적 경로 접두사 → 상품 코드 */
export const ONETAG_ROUTE_PREFIX_PRODUCTS: {
  prefix: string
  productCode: string
  label: string
}[] = [
  {
    prefix: '/blog/post/',
    productCode: 'st_blog_post',
    label: '블로그 · 글 상세',
  },
  {
    prefix: '/service/features/',
    productCode: 'st_service_features',
    label: '서비스 · 기능 스텝',
  },
]

/**
 * 전환 이벤트 (비쇼핑 전환 / 클릭 전환)
 * - bookCreation: /book-creation CTA·리다이렉트
 * - appDownload*: 홈 히어로 스토어 배지 클릭
 */
export const ONETAG_CONVERSIONS = {
  bookCreation: {
    productName: 'book_creation',
    convType: 'etc',
    label: '책 만들기 (/book-creation)',
  },
  appDownloadIos: {
    productName: 'app_download_ios',
    convType: 'etc',
    label: 'iOS App Store 다운로드',
    clickSelector: '[data-onetag-click="app-download-ios"]',
  },
  appDownloadAndroid: {
    productName: 'app_download_android',
    convType: 'etc',
    label: 'Android Google Play 다운로드',
    clickSelector: '[data-onetag-click="app-download-android"]',
  },
} as const satisfies Record<string, OneTagConversion>

/** 모비온 공유용 요약 목록 */
export function listOneTagRouteCatalog(): {
  path: string
  productCode: string
  label: string
}[] {
  return Object.entries(ONETAG_ROUTE_PRODUCTS).map(([path, item]) => ({
    path,
    productCode: item.productCode,
    label: item.label,
  }))
}

export function listOneTagConversionCatalog(): {
  key: string
  productName: string
  convType: OneTagConvType
  label: string
  clickSelector?: string
}[] {
  return Object.entries(ONETAG_CONVERSIONS).map(([key, item]) => ({
    key,
    productName: item.productName,
    convType: item.convType,
    label: item.label,
    clickSelector: 'clickSelector' in item ? item.clickSelector : undefined,
  }))
}

export function resolveOneTagRouteProduct(
  barePath: string
): OneTagRouteProduct | null {
  const exact = ONETAG_ROUTE_PRODUCTS[barePath]
  if (exact) return exact

  for (const item of ONETAG_ROUTE_PREFIX_PRODUCTS) {
    if (barePath.startsWith(item.prefix)) {
      return { productCode: item.productCode, label: item.label }
    }
  }

  return null
}
